import { useMemo } from 'react'

import { keyBy, merge, partition, sortBy, uniqBy } from 'lodash'

import { IAddress, AddressSearchResult } from '~/types'

export function mergeResults({
  contactResults,
  farcasterResults,
  ensResults,
  payeeResults,
}: {
  contactResults: AddressSearchResult[]
  farcasterResults: AddressSearchResult[]
  ensResults: AddressSearchResult[]
  payeeResults: AddressSearchResult[]
}): AddressSearchResult[] {
  const farcasterResultsMap = keyBy(farcasterResults, 'address')
  const contactResultsMap = keyBy(contactResults, 'address')
  const payeeResultsMap = keyBy(payeeResults, 'address')
  const ensResultsMap = keyBy(ensResults, 'address')

  const mergedResults = contactResults.map((contactResult) => {
    const farcasterResult = farcasterResultsMap[contactResult.address]
    const ensResult = ensResultsMap[contactResult.address]
    const payeeResult = payeeResultsMap[contactResult.address]
    return payeeResult
      ? merge({}, contactResult, payeeResult)
      : farcasterResult
        ? merge({}, contactResult, farcasterResult)
        : ensResult
          ? merge({}, contactResult, ensResult)
          : contactResult
  })

  const remainingPayeeResults = payeeResults.filter(
    (payeeResult) => !contactResultsMap[payeeResult.address],
  )
  const remainingFarcasterResults = farcasterResults.filter(
    (farcasterResult) => !contactResultsMap[farcasterResult.address],
  )
  const remainingEnsResults = ensResults.filter(
    (ensResult) => !contactResultsMap[ensResult.address],
  )

  // Combine all remaining results and deduplicate by address
  const allRemainingResults = [
    ...remainingPayeeResults,
    ...remainingEnsResults,
    ...remainingFarcasterResults,
  ]
  const deduplicatedRemainingResults = uniqBy(allRemainingResults, 'address')

  return [...mergedResults, ...deduplicatedRemainingResults]
}

export const useFilteredAddresses = (
  filteredAddressesSet: Set<IAddress>,
  data: AddressSearchResult[],
) => {
  return useMemo(() => {
    return data.filter((result) => !filteredAddressesSet.has(result.address))
  }, [filteredAddressesSet, data])
}

const isInternalResult = (result: AddressSearchResult) =>
  Boolean(result.smartAccountName)

const isContactResult = (result: AddressSearchResult) =>
  Boolean(result.orgLabel)

export const partitionResultsByInternal = (results: AddressSearchResult[]) => {
  const [internal, external] = partition(results, isInternalResult)

  return { internal, external }
}

export const sortInternalFirst = (results: AddressSearchResult[]) => {
  const { internal, external } = partitionResultsByInternal(results)
  return [...internal, ...external]
}

export const filterResultsByQuery = (
  results: AddressSearchResult[],
  query: string,
): AddressSearchResult[] => {
  const normalizedQuery = query.trim().toLowerCase()
  if (!normalizedQuery) return results

  return results.filter(
    (result) =>
      result.smartAccountName?.toLowerCase().includes(normalizedQuery) ||
      result.orgLabel?.toLowerCase().includes(normalizedQuery) ||
      result.address.toLowerCase().includes(normalizedQuery),
  )
}

// Lower rank = better match: exact name, then name prefix, then name
// substring, then address-only matches.
const queryRelevanceRank = (
  result: AddressSearchResult,
  normalizedQuery: string,
): number => {
  const name = (
    result.smartAccountName ??
    result.orgLabel ??
    result.ens?.name ??
    result.farcaster?.username ??
    ''
  ).toLowerCase()

  if (name === normalizedQuery) return 0
  if (name.startsWith(normalizedQuery)) return 1
  if (name.includes(normalizedQuery)) return 2
  return 3
}

// Stable sort, so equally-ranked results keep their incoming order.
export const sortResultsByQueryRelevance = (
  results: AddressSearchResult[],
  query: string,
): AddressSearchResult[] => {
  const normalizedQuery = query.trim().toLowerCase()
  if (!normalizedQuery) return results

  return sortBy(results, (result) =>
    queryRelevanceRank(result, normalizedQuery),
  )
}

// Groups the "All" search view into accounts / banks / contacts / external.
// Search results win the dedupe over account matches; bank addresses are
// excluded (matching banks render as their own group); internal accounts are
// relevance-sorted; labeled contacts split out from the other external results.
export const groupAllResults = ({
  searchResults,
  accountMatches,
  allBanks,
  matchingBanks,
  query,
}: {
  searchResults: AddressSearchResult[]
  accountMatches: AddressSearchResult[]
  allBanks: AddressSearchResult[]
  matchingBanks: AddressSearchResult[]
  query: string
}): {
  accounts: AddressSearchResult[]
  banks: AddressSearchResult[]
  contacts: AddressSearchResult[]
  external: AddressSearchResult[]
} => {
  const bankAddresses = new Set(allBanks.map((result) => result.address))
  const union = uniqBy([...searchResults, ...accountMatches], 'address').filter(
    (result) => !bankAddresses.has(result.address),
  )
  const { internal, external } = partitionResultsByInternal(union)
  const [contacts, nonContacts] = partition(external, isContactResult)

  return {
    accounts: sortResultsByQueryRelevance(internal, query),
    banks: matchingBanks,
    contacts,
    external: nonContacts,
  }
}
